import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import './JournalPage.css';

function JournalPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'gratitude', 'reflection', 'goals', 'learning'
  useEffect(() => { document.title = 'PEC - Diario'; }, []);

  // Mock journal entries data
  const journalEntries = [
    {
      id: 1,
      title: 'Reflexiones de Liderazgo',
      content: 'Hoy tuve una reunión importante donde pude aplicar las técnicas de escucha activa que he estado estudiando. Me di cuenta de que cuando realmente escucho a mi equipo, surgen ideas innovadoras que no había considerado...',
      type: 'reflection',
      mood: 'positive',
      date: '2024-01-15T20:30:00Z',
      tags: ['liderazgo', 'equipo', 'crecimiento'],
      wordCount: 247,
      favorite: true,
      weather: '☀️',
      energy: 'high'
    },
    {
      id: 2,
      title: 'Gratitud Diaria',
      content: 'Tres cosas por las que estoy agradecido hoy: 1) La llamada sorpresa de mi hermana que alegró mi día, 2) El proyecto que finalmente se aprobó después de semanas de trabajo, 3) La caminata matutina que me dio energía para toda la jornada.',
      type: 'gratitude',
      mood: 'grateful',
      date: '2024-01-15T07:15:00Z',
      tags: ['gratitud', 'familia', 'proyecto'],
      wordCount: 156,
      favorite: false,
      weather: '🌤️',
      energy: 'medium'
    },
    {
      id: 3,
      title: 'Metas del Trimestre',
      content: 'Revisando mis objetivos para Q1: 1) Implementar el sistema de automatización - 70% completo, 2) Mejorar las métricas de productividad personal - en progreso, 3) Completar el curso de liderazgo - 40% avanzado. Necesito ajustar mi enfoque...',
      type: 'goals',
      mood: 'focused',
      date: '2024-01-14T19:45:00Z',
      tags: ['metas', 'productividad', 'trimestre'],
      wordCount: 198,
      favorite: true,
      weather: '🌧️',
      energy: 'medium'
    },
    {
      id: 4,
      title: 'Aprendizaje sobre IA',
      content: 'Terminé de leer el artículo sobre transformers y GPT. Conceptos clave: atención, embeddings, fine-tuning. Me fascina cómo estos modelos pueden entender contexto. Quiero experimentar con APIs para crear herramientas personalizadas...',
      type: 'learning',
      mood: 'curious',
      date: '2024-01-14T15:20:00Z',
      tags: ['IA', 'tecnología', 'aprendizaje'],
      wordCount: 134,
      favorite: false,
      weather: '☁️',
      energy: 'high'
    },
    {
      id: 5,
      title: 'Desafíos del Día',
      content: 'Hoy fue particularmente challenging con la presentación que no salió como esperaba. Sin embargo, aprendí que la preparación mental es tan importante como la técnica. La próxima vez voy a implementar ejercicios de respiración antes de presentar...',
      type: 'reflection',
      mood: 'contemplative',
      date: '2024-01-13T21:10:00Z',
      tags: ['desafíos', 'presentación', 'crecimiento'],
      wordCount: 203,
      favorite: false,
      weather: '🌦️',
      energy: 'low'
    },
    {
      id: 6,
      title: 'Momento de Claridad',
      content: 'Durante mi sesión de meditación matutina tuve una epifanía sobre el balance trabajo-vida. He estado tan enfocado en optimizar productividad que olvidé la importancia del descanso activo. Implementar bloques de "no hacer nada" en mi calendario.',
      type: 'reflection',
      mood: 'peaceful',
      date: '2024-01-13T08:00:00Z',
      tags: ['meditación', 'balance', 'claridad'],
      wordCount: 189,
      favorite: true,
      weather: '🌅',
      energy: 'medium'
    }
  ];

  // Filter entries
  const filteredEntries = journalEntries.filter(entry => {
    const matchesSearch = entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = activeFilter === 'all' || entry.type === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  // Calculate stats
  const stats = {
    total: journalEntries.length,
    thisWeek: journalEntries.filter(entry => {
      const entryDate = new Date(entry.date);
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return entryDate >= weekAgo;
    }).length,
    favorites: journalEntries.filter(entry => entry.favorite).length,
    totalWords: journalEntries.reduce((sum, entry) => sum + entry.wordCount, 0)
  };

  const handleEntryAction = (entryId, action) => {
    console.log(`${action} entry:`, entryId);
    // Implement entry actions
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    const icons = {
      'gratitude': '🙏',
      'reflection': '🤔',
      'goals': '🎯',
      'learning': '📚'
    };
    return icons[type] || '📝';
  };

  const getTypeColor = (type) => {
    const colors = {
      'gratitude': 'green',
      'reflection': 'blue',
      'goals': 'orange',
      'learning': 'purple'
    };
    return colors[type] || 'gray';
  };

  const getMoodColor = (mood) => {
    const colors = {
      'positive': 'green',
      'grateful': 'emerald',
      'focused': 'blue',
      'curious': 'purple',
      'contemplative': 'indigo',
      'peaceful': 'teal'
    };
    return colors[mood] || 'gray';
  };

  const getEnergyIcon = (energy) => {
    const icons = {
      'high': '⚡',
      'medium': '🔋',
      'low': '🪫'
    };
    return icons[energy] || '🔋';
  };

  return (
    <div className="journal-page">
      <Header 
        currentPage="journal" 
        showSearch 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showAddButton 
        onAddNew={() => console.log('Nueva entrada')}
      />

      <div className="journal-content">
        {/* Journal Stats */}
        <div className="journal-stats">
          <div className="stat-card total">
            <div className="stat-icon">📖</div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>Total Entradas</p>
            </div>
          </div>
          <div className="stat-card week">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <h3>{stats.thisWeek}</h3>
              <p>Esta Semana</p>
            </div>
          </div>
          <div className="stat-card favorites">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <h3>{stats.favorites}</h3>
              <p>Favoritas</p>
            </div>
          </div>
          <div className="stat-card words">
            <div className="stat-icon">📝</div>
            <div className="stat-content">
              <h3>{stats.totalWords.toLocaleString()}</h3>
              <p>Palabras Escritas</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="journal-filters">
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              📓 Todas ({stats.total})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'gratitude' ? 'active' : ''}`}
              onClick={() => setActiveFilter('gratitude')}
            >
              🙏 Gratitud ({journalEntries.filter(e => e.type === 'gratitude').length})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'reflection' ? 'active' : ''}`}
              onClick={() => setActiveFilter('reflection')}
            >
              🤔 Reflexiones ({journalEntries.filter(e => e.type === 'reflection').length})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'goals' ? 'active' : ''}`}
              onClick={() => setActiveFilter('goals')}
            >
              🎯 Metas ({journalEntries.filter(e => e.type === 'goals').length})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'learning' ? 'active' : ''}`}
              onClick={() => setActiveFilter('learning')}
            >
              📚 Aprendizaje ({journalEntries.filter(e => e.type === 'learning').length})
            </button>
          </div>
          <div className="filter-actions">
            <button className="btn-secondary">Exportar PDF</button>
            <button className="btn-secondary">Análisis</button>
            <button className="btn-secondary">Configurar</button>
          </div>
        </div>

        {/* Journal Entries */}
        <div className="entries-section">
          <div className="section-header">
            <h2>Mis Entradas ({filteredEntries.length})</h2>
            <div className="writing-streak">
              <span className="streak-indicator">
                🔥 Racha de escritura: 12 días
              </span>
            </div>
          </div>

          <div className="entries-list">
            {filteredEntries.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📝</div>
                <h3>No se encontraron entradas</h3>
                <p>Intenta ajustar los términos de búsqueda o crea una nueva entrada</p>
              </div>
            ) : (
              filteredEntries.map((entry) => (
                <div key={entry.id} className={`entry-card ${getTypeColor(entry.type)}`}>
                  <div className="entry-header">
                    <div className="entry-info">
                      <div className="entry-title-row">
                        <div className="entry-type">
                          {getTypeIcon(entry.type)}
                        </div>
                        <h4 className="entry-title">{entry.title}</h4>
                        <div className="entry-metadata">
                          <span className={`mood-badge ${getMoodColor(entry.mood)}`}>
                            {entry.mood}
                          </span>
                          {entry.favorite && <span className="favorite-star">⭐</span>}
                        </div>
                      </div>
                      <div className="entry-details">
                        <span className="detail-item">📅 {formatDate(entry.date)}</span>
                        <span className="detail-item">📊 {entry.wordCount} palabras</span>
                        <span className="detail-item">{entry.weather} {entry.weather}</span>
                        <span className="detail-item">{getEnergyIcon(entry.energy)} {entry.energy}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="entry-body">
                    <p className="entry-content">{entry.content}</p>
                    
                    <div className="entry-tags">
                      {entry.tags.map((tag, index) => (
                        <span key={index} className="entry-tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="entry-actions">
                    <button 
                      className="btn-action primary"
                      onClick={() => handleEntryAction(entry.id, 'read')}
                    >
                      👁️ Leer Completa
                    </button>
                    <button 
                      className="btn-action secondary"
                      onClick={() => handleEntryAction(entry.id, 'edit')}
                    >
                      ✏️ Editar
                    </button>
                    <button 
                      className="btn-action secondary"
                      onClick={() => handleEntryAction(entry.id, 'favorite')}
                    >
                      {entry.favorite ? '⭐ Quitar' : '☆ Favorito'}
                    </button>
                    <button 
                      className="btn-action secondary"
                      onClick={() => handleEntryAction(entry.id, 'share')}
                    >
                      🔗 Compartir
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Writing Tools */}
        <div className="writing-tools-section">
          <h2>Herramientas de Escritura</h2>
          <div className="tools-grid">
            <div className="tool-card">
              <div className="tool-icon">💭</div>
              <h3>Prompts Creativos</h3>
              <p>Generador de ideas para superar el bloqueo del escritor</p>
              <button className="btn-primary">Explorar Prompts</button>
            </div>
            <div className="tool-card">
              <div className="tool-icon">📊</div>
              <h3>Análisis de Patrones</h3>
              <p>Descubre tendencias en tu estado de ánimo y temas</p>
              <button className="btn-primary">Ver Análisis</button>
            </div>
            <div className="tool-card">
              <div className="tool-icon">🎯</div>
              <h3>Objetivos de Escritura</h3>
              <p>Establece metas diarias y semanales de journaling</p>
              <button className="btn-primary">Configurar</button>
            </div>
            <div className="tool-card">
              <div className="tool-icon">☁️</div>
              <h3>Respaldo en la Nube</h3>
              <p>Sincroniza y protege tus reflexiones más valiosas</p>
              <button className="btn-primary">Configurar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JournalPage;
